/**
 * API service for order operations
 * SECURITY: All calculations done on backend
 */

import { getAuthHeaders } from './authHeaders';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

class OrdersAPI {
    /**
     * Create a new order
     * SECURITY: Backend calculates totals, shipping, etc.
     */
    static async createOrder(orderData) {
        console.log(`📦 OrdersAPI: Creating order`);

        try {
            const headers = await getAuthHeaders({
                'Content-Type': 'application/json'
            });
            const response = await fetch(`${API_BASE_URL}/orders`, {
                method: 'POST',
                headers,
                body: JSON.stringify(orderData)
            });

            if (!response.ok) {
                if (response.status === 401) {
                    throw new Error('NOT_AUTHENTICATED');
                }
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            console.log(`✅ OrdersAPI: Order created successfully - ${data.order?.order_id}`);
            return data;
        } catch (error) {
            console.error('❌ OrdersAPI: Error creating order:', error);
            throw error;
        }
    }

    /**
     * Get user's orders
     */
    static async getOrders() {
        console.log(`📋 OrdersAPI: Getting orders`);

        try {
            const headers = await getAuthHeaders();
            const response = await fetch(`${API_BASE_URL}/orders`, {
                headers
            });

            if (!response.ok) {
                if (response.status === 401) {
                    throw new Error('NOT_AUTHENTICATED');
                }
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            console.log(`✅ OrdersAPI: Got ${data.orders?.length || 0} orders`);
            return data;
        } catch (error) {
            console.error('❌ OrdersAPI: Error getting orders:', error);
            throw error;
        }
    }

    /**
     * Get specific order details
     */
    static async getOrderDetails(orderId) {
        console.log(`🔍 OrdersAPI: Getting order ${orderId}`);

        try {
            const headers = await getAuthHeaders();
            const response = await fetch(`${API_BASE_URL}/orders/${orderId}`, {
                headers
            });

            if (!response.ok) {
                if (response.status === 401) {
                    throw new Error('NOT_AUTHENTICATED');
                }
                if (response.status === 404) {
                    throw new Error('ORDER_NOT_FOUND');
                }
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            console.log(`✅ OrdersAPI: Order details retrieved`);
            return data;
        } catch (error) {
            console.error('❌ OrdersAPI: Error getting order details:', error);
            throw error;
        }
    }

    /**
     * Cancel an order
     */
    static async cancelOrder(orderId) {
        console.log(`❌ OrdersAPI: Cancelling order ${orderId}`);

        try {
            const headers = await getAuthHeaders();
            const response = await fetch(`${API_BASE_URL}/orders/${orderId}/cancel`, {
                method: 'PUT',
                headers
            });

            if (!response.ok) {
                if (response.status === 401) {
                    throw new Error('NOT_AUTHENTICATED');
                }
                if (response.status === 404) {
                    throw new Error('ORDER_NOT_FOUND');
                }
                if (response.status === 400) {
                    const data = await response.json();
                    throw new Error(data.detail || 'Cannot cancel order');
                }
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            console.log(`✅ OrdersAPI: Order cancelled successfully`);
            return data;
        } catch (error) {
            console.error('❌ OrdersAPI: Error cancelling order:', error);
            throw error;
        }
    }
}

export default OrdersAPI;
